/**
 * Deaths, presented (IDEAS X33 stages 1 and 1b): a person who dies falls — a ragdoll thrown by a burst, a slump under
 * a bullet's nudge, a collapse otherwise — then lies as a corpse until its time runs out or the battle's cap pushes it,
 * then sinks or fades. The dying unit's view hides as before (the unit itself respawns from the same record); the body
 * is the view's own figure, handed over as it was drawn at the death (384 lever 6), then folded into one mesh once
 * settled. A budget of live ragdolls (the rest a canned topple); none in the browser smoke's lean mode. Reads the match,
 * writes nothing.
 */
import * as THREE from 'three';
import type { PlayerState } from '../types';
import { setPersonTier, type PersonModel } from '../rendering/personModel';
import type { PersonView } from '../rendering/personView';
import { DeathRagdoll, CannedFall, bakeCorpse, corpseMaterialShared, type DeathPush, type DeathWorld, type Falling } from '../rendering/deathRagdoll';
import { disposeObjectTree } from '../rendering/meshBake';
import { CorpseStore, type CorpseConfig, type DeathCause } from './corpseStore';

/** Live ragdolls at once; past it a death topples (CannedFall). */
export const LIVE_RAGDOLLS = 6;

interface Body {
  corpseId: number;
  holder: THREE.Group;
  model: PersonModel | null;      // while it falls
  fall: Falling | null;
  ragdoll: boolean;
  mesh: THREE.Mesh | null;        // once settled
  up: THREE.Vector3;
  sinkDepth: number;
  material?: THREE.MeshLambertMaterial;   // its own, while fading out
}

export class DeathPresenter {
  private readonly store: CorpseStore;
  private readonly bodies = new Map<number, Body>();

  constructor(private readonly scene: THREE.Scene, config: CorpseConfig, private readonly lean: boolean) {
    this.store = new CorpseStore(config);
  }

  /** How many are falling as ragdolls now (the budget's count). */
  get liveRagdolls(): number { let n = 0; for (const b of this.bodies.values()) if (b.ragdoll && b.fall && !b.fall.settled) n++; return n; }
  get count(): number { return this.bodies.size; }

  /** A unit died at match time `t`: its body falls from the pose its view last drew. */
  onDeath(p: PlayerState, view: PersonView, push: DeathPush, cause: DeathCause, world: DeathWorld, t: number): void {
    const corpse = this.store.add(p.id, p.teamIndex, cause, t);
    // 67.56: the body keeps what the view wore (the helmet or the balaclava, the vest, the leader's kit); 384 lever 6: it is the
    // view's own figure, posed as last drawn, handed over (it was built again from scratch, ~4.5 ms of the death's frame) —
    // drawn in full, holding nothing (the guns, the drone's slate, a grenade, the bandage or the tool)
    const model = view.handOver();
    setPersonTier(model, 'full');
    model.root.visible = true;
    for (const w of Object.values(model.weapons)) if (w) w.group.visible = false;
    for (const o of [model.controller, model.kit.roll, model.kit.dressing, model.kit.inHand.he?.group, model.kit.inHand.smoke?.group, model.kit.tool?.group]) if (o) o.visible = false;
    const holder = new THREE.Group();
    holder.position.copy(view.holder.position); holder.quaternion.copy(view.holder.quaternion);
    holder.add(model.root);
    this.scene.add(holder);
    const facing = new THREE.Vector3(0, 0, 1).applyQuaternion(view.holder.quaternion);
    const ragdoll = !this.lean && this.liveRagdolls < LIVE_RAGDOLLS;
    const fall: Falling = ragdoll ? new DeathRagdoll(model, world, push) : new CannedFall(model, world, push, facing);
    this.bodies.set(corpse.id, { corpseId: corpse.id, holder, model, fall, ragdoll, mesh: null, up: world.up.clone(), sinkDepth: 0.6 * world.personHeight });
  }

  /** Each frame: falls advance and settle into corpses; corpses age, sink or fade, and go. */
  update(dt: number, t: number): void {
    for (const b of this.bodies.values()) {
      if (!b.fall || !b.model) continue;
      if (!b.fall.step(dt)) continue;
      // settled: fold into one mesh where the body lies, then drop the falling body (its holder, or the topple's pivot)
      const anchor = b.model.hips.getWorldPosition(new THREE.Vector3());
      const mesh = bakeCorpse(b.model.root, anchor);
      const top = this.topOf(b);
      top.removeFromParent();
      disposeObjectTree(top);
      this.scene.add(mesh);
      b.mesh = mesh; b.model = null; b.fall = null;
    }
    const { gone, state } = this.store.tick(t);
    for (const c of gone) this.remove(c.id);
    for (const { corpse, going } of state) {
      const b = this.bodies.get(corpse.id);
      if (!b?.mesh || going <= 0) continue;
      if (this.store.config.fade === 'sink') {
        b.mesh.position.addScaledVector(b.up, -(going * b.sinkDepth - (b.mesh.userData.sunk ?? 0)));
        b.mesh.userData.sunk = going * b.sinkDepth;
      } else {
        if (!b.material) { b.material = corpseMaterialShared().clone(); b.material.transparent = true; b.material.depthWrite = false; b.mesh.material = b.material; }
        b.material.opacity = 1 - going;
      }
    }
  }

  /** The falling body's top node: its holder, or the canned fall's pivot that holds it. */
  private topOf(b: Body): THREE.Object3D {
    return b.holder.parent && b.holder.parent !== this.scene ? b.holder.parent : b.holder;
  }

  private remove(id: number): void {
    const b = this.bodies.get(id);
    if (!b) return;
    if (b.mesh) { b.mesh.removeFromParent(); b.mesh.geometry.dispose(); b.material?.dispose(); }
    if (b.model) { const top = this.topOf(b); top.removeFromParent(); disposeObjectTree(top); }
    this.bodies.delete(id);
  }

  dispose(): void {
    for (const c of this.store.clear()) this.remove(c.id);
    for (const id of [...this.bodies.keys()]) this.remove(id);
  }
}
